import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus, Wind, Sparkles } from 'lucide-react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { Mascot } from '@/components/Mascot';
import { Loader } from '@/components/Loader';
import {
  loadMonthTrend, firstCheckinDate, enableTrend,
  MOOD_COLOR, SLEEP_COLOR, MOOD_EMOJI, SLEEP_EMOJI, MOOD_LABEL, SLEEP_LABEL,
  type MonthTrend, type MoodDay,
} from '@/lib/moodTrend';

/**
 * «Мой месяц» — куда смотреть после ежедневной отметки (10.10.2026, идея Славы).
 *
 * Мозаика месяца отвечает на «как прошёл месяц» одним взглядом, график — на «куда
 * идёт линия», заметки — на «от чего это зависит». Ни одного запроса к модели.
 *
 * Анти-стыд: пустые дни — это «не отметил(а)», а не провал; тяжёлые дни подаются
 * как сигнал, а не как оценка. Релакс предлагается тому, у кого линия просела
 * или кто только что включил тренд, — не всем подряд.
 */

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_DAT = ['январю', 'февралю', 'марту', 'апрелю', 'маю', 'июню', 'июлю', 'августу', 'сентябрю', 'октябрю', 'ноябрю', 'декабрю'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const DOW = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

type Mode = 'mood' | 'sleep';

export default function MoodMonth() {
  const nav = useNavigate();
  const { profile, setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [data, setData] = useState<MonthTrend | null>(null);
  const [first, setFirst] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('mood');
  const [picked, setPicked] = useState<string | null>(null);
  const [justEnabled, setJustEnabled] = useState(false);

  const tracking = !!profile?.trend_tracking_at;

  useEffect(() => {
    if (!profile?.id) return;
    firstCheckinDate(profile.id).then(setFirst);
  }, [profile?.id]);

  useEffect(() => {
    if (!profile?.id) return;
    setData(null);
    setPicked(null);
    loadMonthTrend(profile.id, ym.y, ym.m).then(setData);
  }, [profile?.id, ym.y, ym.m]);

  const isCurrent = ym.y === now.getFullYear() && ym.m === now.getMonth();
  const canPrev = !!first && `${ym.y}-${String(ym.m + 1).padStart(2, '0')}` > first.slice(0, 7);

  function shift(delta: number) {
    haptic('light');
    const d = new Date(ym.y, ym.m + delta, 1);
    setYm({ y: d.getFullYear(), m: d.getMonth() });
  }

  async function turnOn() {
    if (!profile?.id) return;
    haptic('medium');
    const at = await enableTrend(profile.id);
    if (at) {
      setProfile({ ...profile, trend_tracking_at: at });
      setJustEnabled(true);
    }
  }

  // Сетка месяца: недели с понедельника
  const cells = useMemo(() => {
    const firstDow = (new Date(ym.y, ym.m, 1).getDay() + 6) % 7;
    const count = new Date(ym.y, ym.m + 1, 0).getDate();
    const byDate = new Map((data?.days ?? []).map((d) => [d.date, d]));
    const out: ({ day: number; date: string; row: MoodDay | null; future: boolean } | null)[] = [];
    for (let i = 0; i < firstDow; i++) out.push(null);
    for (let day = 1; day <= count; day++) {
      const date = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      out.push({ day, date, row: byDate.get(date) ?? null, future: isCurrent && day > now.getDate() });
    }
    return out;
  }, [data, ym.y, ym.m]);

  const chart = useMemo(() => {
    // Текущий месяц рисуем до сегодняшнего дня: иначе десять точек жмутся в левую треть
    const full = new Date(ym.y, ym.m + 1, 0).getDate();
    const count = isCurrent ? Math.min(full, Math.max(now.getDate(), 7)) : full;
    const byDay = new Map((data?.days ?? []).map((d) => [Number(d.date.slice(8)), d]));
    return Array.from({ length: count }, (_, i) => {
      const r = byDay.get(i + 1);
      return { day: i + 1, sleep: r?.sleep ?? null, mood: r?.mood ?? null };
    });
  }, [data, ym.y, ym.m]);

  const pickedRow = picked ? data?.days.find((d) => d.date === picked) ?? null : null;
  // Паузу предлагаем, когда линия просела или тяжёлого накопилось много — и не тогда,
  // когда она сама идёт вверх: «тебе нужна пауза» рядом с «всё растёт» звучит как упрёк.
  const needsRest = !!data && (
    data.moodDir === 'down' || data.sleepDir === 'down'
    || (data.heavyDays >= 3 && data.moodDir !== 'up')
    || (data.badNights >= 3 && data.sleepDir !== 'up')
  );
  const prevName = MONTHS_DAT[(ym.m + 11) % 12];

  return (
    <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => nav(-1)}
          className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
          aria-label="Назад"
        >
          <ChevronLeft size={18} />
        </button>
        <div>
          <div className="font-display text-lg font-bold leading-tight">Мой месяц</div>
          <div className="text-xs text-ink-dim">Сон и настроение по твоим отметкам</div>
        </div>
      </div>

      {/* Месяц */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => shift(-1)}
          disabled={!canPrev}
          className="w-9 h-9 rounded-xl bg-bg-card border border-line flex items-center justify-center disabled:opacity-30"
          aria-label="Предыдущий месяц"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="font-display font-semibold">{MONTHS[ym.m]} {ym.y}</div>
        <button
          onClick={() => shift(1)}
          disabled={isCurrent}
          className="w-9 h-9 rounded-xl bg-bg-card border border-line flex items-center justify-center disabled:opacity-30"
          aria-label="Следующий месяц"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {!data ? (
        <Loader />
      ) : (
        <div className="space-y-4">
          {/* Дофи — одна фраза про главное */}
          <div className="card p-3.5 flex items-start gap-3">
            <Mascot pose={data.moodDir === 'up' || data.sleepDir === 'up' ? 'celebrate' : needsRest ? 'support' : 'calm'} compact className="w-11 h-11 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm leading-snug">{data.dofiLine}</p>
              <p className="text-[11px] text-ink-dim mt-1">
                Отмечено {data.marked} из {data.elapsed} {isCurrent ? 'прошедших дней' : 'дней месяца'}
              </p>
            </div>
          </div>

          {/* Включить тренд — человек понимает, зачем отмечает */}
          {!tracking && (
            <div className="rounded-2xl border border-emerald-glow/30 bg-emerald-glow/5 p-4">
              <div className="font-display font-bold text-sm">Отслеживать тренд</div>
              <p className="text-xs text-ink-dim mt-1 leading-relaxed">
                Каждая отметка сна и дня — точка на этом графике. Включи, и я буду
                показывать, куда идёт линия и от чего она зависит.
              </p>
              <button
                onClick={turnOn}
                className="w-full mt-3 py-2.5 rounded-xl bg-emerald-glow text-bg text-sm font-medium active:scale-[0.99] transition-transform flex items-center justify-center gap-2"
              >
                <TrendingUp size={16} /> Отслеживать тренд
              </button>
            </div>
          )}

          {justEnabled && <RelaxOffer title="Тренд включён" text="Хочешь, чтобы линия шла вверх? Самый быстрый рычаг — две-три минуты дыхания вечером. Попробуй сегодня и отметь день: посмотрим, сдвинется ли точка." />}

          {/* Средние */}
          <div className="grid grid-cols-2 gap-3">
            <Tile title="Сон" emoji={data.sleepAvg ? SLEEP_EMOJI[Math.round(data.sleepAvg)] : '🌙'} value={data.sleepAvg} delta={data.sleepDelta} dir={data.sleepDir} prevName={prevName} color="#3DD8FF" />
            <Tile title="День" emoji={data.moodAvg ? MOOD_EMOJI[Math.round(data.moodAvg)] : '🙂'} value={data.moodAvg} delta={data.moodDelta} dir={data.moodDir} prevName={prevName} color="#10E58E" />
          </div>

          {/* Мозаика месяца */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-sm">Месяц одним взглядом</div>
              <div className="flex rounded-lg bg-bg-elevated p-0.5 text-[11px]">
                {(['mood', 'sleep'] as Mode[]).map((k) => (
                  <button
                    key={k}
                    onClick={() => { setMode(k); haptic('light'); }}
                    className={`px-2.5 py-1 rounded-md transition-colors ${mode === k ? 'bg-bg text-ink' : 'text-ink-dim'}`}
                  >
                    {k === 'mood' ? 'День' : 'Сон'}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {DOW.map((d) => (
                <div key={d} className="text-[10px] text-ink-dim text-center pb-0.5">{d}</div>
              ))}
              {cells.map((c, i) => {
                if (!c) return <div key={`e${i}`} />;
                const v = mode === 'mood' ? c.row?.mood : c.row?.sleep;
                const color = v ? (mode === 'mood' ? MOOD_COLOR : SLEEP_COLOR)[v] : null;
                const on = picked === c.date;
                return (
                  <button
                    key={c.date}
                    disabled={c.future}
                    onClick={() => { setPicked(on ? null : c.date); haptic('light'); }}
                    className={`aspect-square rounded-lg flex items-center justify-center text-[11px] font-medium transition-transform active:scale-95 ${
                      on ? 'ring-2 ring-white/80' : ''
                    } ${color ? 'text-bg' : c.future ? 'text-ink-dim/40' : 'text-ink-dim border border-dashed border-line'}`}
                    style={color ? { background: color } : undefined}
                  >
                    {c.day}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-3 text-[10px] text-ink-dim">
              <span>{mode === 'mood' ? 'тяжело' : 'плохо спал(а)'}</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((v) => (
                  <span key={v} className="w-4 h-2 rounded-sm" style={{ background: (mode === 'mood' ? MOOD_COLOR : SLEEP_COLOR)[v] }} />
                ))}
              </div>
              <span>{mode === 'mood' ? 'отлично' : 'выспался(лась)'}</span>
            </div>

            {picked && (
              <div className="mt-3 pt-3 border-t border-line/50 text-xs">
                <div className="text-ink-dim mb-1">
                  {new Date(picked + 'T12:00:00').toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
                {pickedRow ? (
                  <div className="space-y-1">
                    {pickedRow.sleep != null && <div>{SLEEP_EMOJI[pickedRow.sleep]} Сон: {SLEEP_LABEL[pickedRow.sleep].toLowerCase()}</div>}
                    {pickedRow.mood != null && <div>{MOOD_EMOJI[pickedRow.mood]} День: {MOOD_LABEL[pickedRow.mood].toLowerCase()}</div>}
                    {pickedRow.tags?.length > 0 && <div className="text-ink-dim">{pickedRow.tags.join(' · ')}</div>}
                    {pickedRow.note && <div className="text-ink-muted italic">«{pickedRow.note}»</div>}
                  </div>
                ) : (
                  <div className="text-ink-dim">Отметки нет — это не пропуск, просто день без записи.</div>
                )}
              </div>
            )}
          </div>

          {/* Линия месяца */}
          {data.marked >= 2 && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-bold text-sm">Куда идёт линия</div>
                <div className="flex items-center gap-3 text-[10px] text-ink-dim">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#3DD8FF' }} />сон</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#10E58E' }} />день</span>
                </div>
              </div>
              <div className="h-40 -ml-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chart} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="day" tick={{ fill: '#5A6573', fontSize: 10 }} tickLine={false} axisLine={false} interval={chart.length > 16 ? 4 : chart.length > 8 ? 1 : 0} />
                    <YAxis domain={[1, 5]} ticks={[1, 3, 5]} tick={{ fill: '#5A6573', fontSize: 10 }} tickLine={false} axisLine={false} width={26} />
                    <Tooltip
                      contentStyle={{ background: '#12181D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12 }}
                      labelFormatter={(d) => `${d} ${MONTHS_GEN[ym.m]}`}
                      formatter={(v: any, name: any) => [name === 'sleep' ? SLEEP_LABEL[v] : MOOD_LABEL[v], name === 'sleep' ? 'Сон' : 'День']}
                    />
                    <Line type="monotone" dataKey="sleep" stroke="#3DD8FF" strokeWidth={2} dot={{ r: 2.5, fill: '#3DD8FF', strokeWidth: 0 }} connectNulls isAnimationActive={false} />
                    <Line type="monotone" dataKey="mood" stroke="#10E58E" strokeWidth={2.5} dot={{ r: 3, fill: '#10E58E', strokeWidth: 0 }} connectNulls isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-ink-dim mt-1">Шкала от 1 до 5. Точки — дни с отметкой, линия соединяет соседние отметки.</p>
            </div>
          )}

          {/* Что заметил */}
          {data.insights.length > 0 && (
            <div className="card p-4">
              <div className="font-display font-bold text-sm mb-2.5">Что я заметил</div>
              <div className="space-y-3">
                {data.insights.map((i) => (
                  <div key={i.id} className="flex gap-2.5">
                    <span className="text-base leading-none mt-0.5">{i.emoji}</span>
                    <div>
                      <p className="text-sm leading-snug">{i.text}</p>
                      <p className="text-[10px] text-ink-dim mt-0.5">{i.basis}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-ink-muted mt-3 leading-snug">
                Это наблюдения по твоим отметкам, а не выводы врача: на десятке дней
                совпадения случаются сами по себе.
              </p>
            </div>
          )}

          {data.marked > 0 && data.marked < 4 && (
            <p className="text-xs text-ink-dim text-center px-4">
              Заметки о том, от чего зависит твой день, появятся, когда отметок станет больше.
            </p>
          )}

          {needsRest && !justEnabled && (
            <RelaxOffer
              title="Телу нужна пауза"
              text={
                data.badNights >= 3 && data.heavyDays < 3
                  ? `Ночей, после которых не было сил: ${data.badNights}. Попробуй пару минут спокойного дыхания перед сном — и отметь утром, как спалось.`
                  : `Тяжёлых дней в этом месяце: ${data.heavyDays}. Две-три минуты дыхания вечером — самый короткий способ сбросить напряжение. Попробуй и отметь день.`
              }
            />
          )}

          <button
            onClick={() => { haptic('light'); nav('/week-review'); }}
            className="w-full py-3 rounded-xl bg-bg-card border border-line text-sm text-ink-muted flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
          >
            <Sparkles size={15} className="text-emerald-glow" /> Разбор недели: как состояние связано с едой
          </button>
        </div>
      )}
    </div>
  );
}

function Tile({
  title, emoji, value, delta, dir, prevName, color,
}: {
  title: string; emoji: string; value: number | null; delta: number | null;
  dir: 'up' | 'down' | 'flat' | null; prevName: string; color: string;
}) {
  const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
  return (
    <div className="card p-3.5">
      <div className="flex items-center justify-between">
        <span className="label">{title}</span>
        <span className="text-lg leading-none">{emoji}</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className="font-display text-2xl font-bold" style={{ color: value != null ? color : undefined }}>
          {value != null ? value.toFixed(1).replace('.', ',') : '—'}
        </span>
        <span className="text-xs text-ink-dim">из 5</span>
      </div>
      <div className="mt-1 text-[11px] text-ink-dim min-h-[30px] leading-snug">
        {dir && (
          <span className="flex items-center gap-1">
            <Icon size={12} className={dir === 'up' ? 'text-emerald-glow' : dir === 'down' ? 'text-over' : ''} />
            {dir === 'up' ? 'линия идёт вверх' : dir === 'down' ? 'линия идёт вниз' : 'держится ровно'}
          </span>
        )}
        {delta != null && delta !== 0 && (
          <span>{delta > 0 ? '+' : '−'}{Math.abs(delta).toFixed(1).replace('.', ',')} к {prevName}</span>
        )}
        {value == null && <span>пока нет отметок</span>}
      </div>
    </div>
  );
}

/** Мостик в релакс: одна строка-предложение и два входа — быстрый и общий */
function RelaxOffer({ title, text }: { title: string; text: string }) {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  return (
    <div className="rounded-2xl border border-sky-glow/25 bg-sky-glow/5 p-4">
      <div className="font-display font-bold text-sm">{title}</div>
      <p className="text-xs text-ink-dim mt-1 leading-relaxed">{text}</p>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => { haptic('medium'); nav('/breathing/box'); }}
          className="flex-1 py-2.5 rounded-xl bg-sky-glow/15 border border-sky-glow/30 text-xs font-medium flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
        >
          <Wind size={14} /> Дыхание, 3 минуты
        </button>
        <button
          onClick={() => { haptic('light'); nav('/chat?tab=relax'); }}
          className="flex-1 py-2.5 rounded-xl bg-bg-card border border-line text-xs text-ink-muted active:scale-[0.98] transition-transform"
        >
          Весь релакс
        </button>
      </div>
    </div>
  );
}
